// Multi-environment configuration constants

export type Stage = 'beta' | 'gamma' | 'prod';

export interface EnvironmentConfig {
  webSocketUrl: string;
  emailSendUrl: string;
  cognitoConfig: {
    userPoolId: string;
    clientId: string;
    domain: string;
    redirectUri: string;
  };
}

// Get current stage from URL hostname
export const getCurrentStage = (): Stage => {
  // First check environment variable
  const envStage = process.env.REACT_APP_STAGE as Stage;
  if (envStage) {
    return envStage;
  }

  // Detect from hostname
  const hostname = window.location.hostname;

  if (hostname.includes('beta.mrpibb.ai.amazon.dev') || hostname.includes('localhost')) {
    return 'beta';
  } else if (hostname.includes('gamma.mrpibb.ai.amazon.dev')) {
    return 'gamma';
  } else if (hostname.includes('mrpibb.ai.amazon.dev')) {
    return 'prod';
  }

  // If we can't determine stage, throw error
  throw new Error(`Cannot determine stage from hostname: ${hostname}. Expected beta.mrpibb.ai.amazon.dev, gamma.mrpibb.ai.amazon.dev, or mrpibb.ai.amazon.dev`);
};

// Environment-specific configurations
const environments: Record<Stage, EnvironmentConfig> = {
  beta: {
    webSocketUrl: 'wss://app.beta.soda-brain.ai.amazon.dev',
    emailSendUrl: 'https://api.beta.mrpibb.ai.amazon.dev/send',
    cognitoConfig: {
      userPoolId: 'us-east-1_oWDG5H0Mt',
      clientId: '39a7rrmeskps06nkrs7fc184gf',
      domain: 'mr-pibb-auth-beta.auth.us-east-1.amazoncognito.com',
      redirectUri: window.location.origin,
    },
  },
  gamma: {
    webSocketUrl: 'wss://app.gamma.soda-brain.ai.amazon.dev',
    emailSendUrl: 'https://api.gamma.mrpibb.ai.amazon.dev/send',
    cognitoConfig: {
      userPoolId: 'us-east-1_cKykLpzES',
      clientId: 'rlqs0dok9osh76rpou7k0lmeu',
      domain: 'mr-pibb-auth-gamma.auth.us-east-1.amazoncognito.com',
      redirectUri: window.location.origin,
    },
  },
  prod: {
    webSocketUrl: 'wss://app.soda-brain.ai.amazon.dev',
    emailSendUrl: 'https://api.mrpibb.ai.amazon.dev/send',
    cognitoConfig: {
      userPoolId: 'us-east-1_ZsgDUZdGU',
      clientId: '7tgii6h10st67m60mh1c2g9u9h',
      domain: 'mr-pibb-auth-prod.auth.us-east-1.amazoncognito.com',
      redirectUri: window.location.origin,
    },
  },
};

// Get configuration for current environment
export const getEnvironmentConfig = (): EnvironmentConfig => {
  const stage = getCurrentStage();
  return environments[stage];
};

// Export individual configs for convenience
export const WS_URL = getEnvironmentConfig().webSocketUrl;
export const API_URL = getEnvironmentConfig().webSocketUrl.replace('wss://', 'https://');
export const EMAIL_SEND_URL = getEnvironmentConfig().emailSendUrl;
export const COGNITO_CONFIG = getEnvironmentConfig().cognitoConfig;

// Per-stage stall-detector threshold for boot phases (see useBootSequence).
// This is NOT a minimum display time — it only bounds how long we wait
// before treating a phase as stuck and surfacing a retry option. Beta uses
// a shorter threshold so a genuinely hung phase is diagnosable faster
// during iteration; prod/gamma default to a more patient value that
// tolerates cold starts and slower networks.
const BOOT_PHASE_STALL_MS: Record<Stage, number> = {
  beta: 4_000,
  gamma: 8_000,
  prod: 8_000,
};

export const getBootPhaseStallMs = (): number => BOOT_PHASE_STALL_MS[getCurrentStage()];

// Label for the roadmap button. Reused as the title of the
// new conversation the button opens, so the button text and the conversation
// name in the sidebar always match. Single source of truth.
export const ROADMAP_BUTTON_LABEL = "🥤 What's on our data roadmap?";

// Prompt sent by the roadmap sidebar button. Names the source doc
// (SodaBrain's data-availability.md) and its four columns so SodaBrain
// reproduces the Milestone Summary table verbatim as markdown rather than
// paraphrasing it. Single source of truth — referenced by App.tsx.
export const ROADMAP_PROMPT =
  'Show me the Milestone Summary table from the AI-Ready Datasets roadmap (data-availability.md) as a markdown table with the Milestone, Scope, Target, and Status columns.';
